import React from 'react';
import {
  BodyText,
  BoldBodyText,
  Button,
  FlexRow,
  Grid,
  IconType,
  ISpruceFieldProps,
  ModalWindow,
  Notification,
  RotatingIcon,
  SpruceField,
  SwitchDialogWindows,
} from 'ui-toolkit';
import styled from 'styled-components';
import { DocumentData } from './DocumentData';
import { DocumentModalTitle } from './DocumentModalTitle';
import { IUploadDocument } from 'types/order/documents/upload/interfaces';
import { useFormikContext } from 'formik';
import { DocumentCategorySelector } from '../categorySelect/DocumentCategorySelector';
import { IDocumentCategory } from '../../../../types/order/documents/IDocumentCategoryType';
import { requiresGoodThroughDate } from '../../../../types/order/documents/IDocumentCategoryType';

const StyledDocumentFile = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5} 0;
`;

const StyledModalContent = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
`;

export interface IUploadDocumentProps {
  documentCategoriesByName: Record<string, IDocumentCategory>;
  documentCategoryFilter?: (category: IDocumentCategory) => boolean;
  isLoading: boolean;
  isComplete: boolean;
  hasError: boolean;
  formIsValid: boolean;
  index: number;
  onDeleteDocument: () => void;
  onResetForm: () => void;
  //TODO: remove pass through onSubmit prop and instead make sure <Form/> is rendered inside of the <Modal/> component
  onSubmit: () => void;
}

export const UploadDocument: React.FC<IUploadDocumentProps> = ({
  documentCategoriesByName,
  documentCategoryFilter,
  isLoading,
  isComplete,
  hasError,
  formIsValid,
  index,
  onDeleteDocument,
  onResetForm,
  onSubmit,
}) => {
  const formik = useFormikContext<{ files: IUploadDocument[] }>();
  const document = formik.values.files[index];
  const showModal = formik.values.files[index]
    ? requiresGoodThroughDate(formik.values.files[index].category, documentCategoriesByName)
    : false;

  const onCloseModal = () => {
    onDeleteDocument();
    onResetForm();
  };
  const onDocumentCategorySelect = async (category: IDocumentCategory) => {
    const newDocument = {
      ...document,
      category: category.name,
      isRepeatable: category.isRepeatable,
      metadata: document && category.isRepeatable ? document.metadata : undefined,
    } as IUploadDocument;

    const newValue = [...formik.values.files];
    newValue.splice(index, 1, newDocument);
    formik.setFieldValue('files', newValue);
  };

  return (
    <StyledDocumentFile>
      {!isComplete ? (
        <BodyText>
          Select a Category for <BoldBodyText as="span">{document.file.name}</BoldBodyText>
        </BodyText>
      ) : null}
      <div>
        {showModal || (!isLoading && !isComplete) ? (
          <SpruceField name={`files[${index}].category`} inputType={'select'}>
            {({ field }: ISpruceFieldProps) => (
              <DocumentCategorySelector
                {...field}
                categoryFilter={documentCategoryFilter}
                onChange={(option: any) => onDocumentCategorySelect(option.value)}
                placeholder="Start typing to select a category"
              />
            )}
          </SpruceField>
        ) : isLoading ? (
          <FlexRow>
            <RotatingIcon type={IconType.Loading} />
            <BodyText>Uploading your file...</BodyText>
          </FlexRow>
        ) : !hasError ? (
          <Notification type="success">Your file has been uploaded</Notification>
        ) : (
          <Notification type="error">Your file could not be uploaded.</Notification>
        )}
      </div>
      {showModal ? (
        <SwitchDialogWindows
          isCompleted={isComplete}
          isError={hasError}
          isLoading={isLoading}
          successTitle={'Your file has been uploaded'}
          errorTitle={'Your file could not be uploaded.'}
          loadingTitle={'Uploading'}
          onCloseClick={onCloseModal}
        >
          <ModalWindow
            contentJustifyContent={'start'}
            contentTextAlign={'left'}
            minHeight={400}
            onClose={onCloseModal}
            title={
              <DocumentModalTitle superText={document.file.name}>
                Finish uploading {documentCategoriesByName[document.category!].label}
              </DocumentModalTitle>
            }
            uitype="fit"
            width={580}
          >
            <StyledModalContent>
              <SpruceField name={`files[${index}].metadata`}>
                {({ field }: ISpruceFieldProps) => {
                  const error =
                    (formik.touched.files as any)[index] &&
                    (formik.touched.files as any)[index].metadata &&
                    formik.errors.files &&
                    formik.errors.files[index] &&
                    (formik.errors.files[index] as any).metadata
                      ? (formik.errors.files[index] as any).metadata.good_through_date
                      : null;
                  return (
                    <DocumentData
                      document={document}
                      formValue={field.value}
                      errorText={error}
                      onChange={field.onChange}
                      showInputs={true}
                      documentCategoriesByName={documentCategoriesByName}
                    />
                  );
                }}
              </SpruceField>
              <Grid columnCount={2} columnWidth={'max-content'}>
                <Button
                  intent={'primary'}
                  type={'button'}
                  disabled={!formIsValid}
                  onClick={() => {
                    if (formIsValid) onSubmit();
                  }}
                >
                  Finish Upload
                </Button>
                <Button intent={'secondary'} type={'reset'} onClick={onCloseModal}>
                  Cancel
                </Button>
              </Grid>
            </StyledModalContent>
          </ModalWindow>
        </SwitchDialogWindows>
      ) : null}
    </StyledDocumentFile>
  );
};
